/**
 * EditorJS wiki blocks → the plain SMS text the BACKEND will actually send.
 *
 * ⚠️ FE/BE TWIN — keep in sync with `getContentFromWikiTemplate` in
 * `teamio-utility/utility.js`. This is the frontend's answer to "what will the patient
 * receive?", and it must agree with the renderer that does the real send. Where they drift,
 * the app either blocks a template that would have worked or green-lights one that will not.
 *
 * The header rule is the subtle part, and it caused a PROD outage on 2026-08-04:
 *
 *   A header block is normally the template's TITLE, so it is excluded from the SMS body.
 *   But when the author typed the WHOLE message as a heading — which the wiki editor makes
 *   easy — excluding headers throws the entire message away. Ten Tulip Tree Dental Care
 *   templates were in exactly that state, each holding complete patient-ready copy in a
 *   single `type:"header"` block. The backend rendered them to nothing and crashed.
 *
 * So: render WITHOUT headers first; only if that yields nothing, render again WITH them.
 * Well-formed templates keep losing their title; header-only templates keep their message.
 */

/**
 * Decode HTML entities and strip tags. Uses the DOM in the browser (correct for every entity
 * the editor can emit) and falls back to a regex pass under Node so this stays unit-testable.
 */
function stripAndDecode(html) {
  const withoutTags = String(html || '').replace(/<[^>]*>?/gm, '');

  if (typeof document !== 'undefined' && document.createElement) {
    const tmp = document.createElement('div');
    tmp.innerHTML = withoutTags;
    return tmp.textContent || tmp.innerText || '';
  }

  // Node fallback — the entities the wiki editor actually produces.
  return withoutTags
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&#(\d+);/g, (_m, code) => String.fromCharCode(parseInt(code, 10)));
}

/**
 * Pull every piece of text out of one block. Mirrors the backend's `extractBlockContent`.
 */
function extractBlockContent(block) {
  const contents = [];
  if (!block || !block.data) return contents;

  const data = block.data;

  if (data.text) contents.push(data.text);

  if (Array.isArray(data.items)) {
    // Checklists carry objects with a .text; plain lists carry strings.
    if (data.items.length && data.items[0] && typeof data.items[0] === 'object' && data.items[0].text) {
      return data.items.map((item) => item.text);
    }
    contents.push(...data.items);
  }

  if (Array.isArray(data.content)) {
    data.content.forEach((row) => {
      if (Array.isArray(row)) contents.push(...row);
    });
  }

  if (data.caption) contents.push(data.caption);

  return contents;
}

function renderBlocks(blocks, skipHeaders) {
  let out = '';
  for (const b of blocks) {
    if (skipHeaders && b && b.type === 'header') continue;
    for (const piece of extractBlockContent(b)) {
      if (!piece || String(piece).trim() === '') continue;
      const decoded = stripAndDecode(piece);
      if (decoded.trim() === '') continue;
      out = out ? out + '\n' + decoded : decoded;
    }
  }
  return out.replace(/\s+$/, '');
}

/**
 * @param {Array} blocks EditorJS blocks
 * @returns {string} the SMS body the backend will send; '' means "nothing to send"
 */
export function extractSmsTextFromBlocks(blocks) {
  if (!Array.isArray(blocks)) return '';

  const text = renderBlocks(blocks, true);
  if (text !== '') return text;

  // Header-only rescue — see the note at the top of this file.
  if (blocks.some((b) => b && b.type === 'header')) return renderBlocks(blocks, false);

  return '';
}

/**
 * Same, from a raw EditorJS JSON string (the `Content` column).
 */
export function extractSmsTextFromContent(rawContent) {
  if (!rawContent) return '';
  let parsed;
  try {
    parsed = typeof rawContent === 'string' ? JSON.parse(rawContent) : rawContent;
  } catch (e) {
    return '';
  }
  return extractSmsTextFromBlocks(parsed && parsed.blocks);
}

export default { extractSmsTextFromBlocks, extractSmsTextFromContent };
